import React, { useState, useEffect, useRef, useMemo } from "react";
import { Typography, TextField, Button, Container, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@mui/material";
import ButtonLink from "../../utils/ButtonLink";
import { Link, useLocation, useNavigate } from "react-router-dom";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

import { getAllDrugs, searchDrugs } from "../../services/drugDataService";

export default function Hero() {
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [searchError, setSearchError] = useState("");
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [requestEmail, setRequestEmail] = useState("");
  const [requestStatus, setRequestStatus] = useState({ loading: false, success: false, error: "" });
  // All loaded drug data (from backend or JSON fallback)
  const [allDrugs, setAllDrugs] = useState([]);
  const navigate = useNavigate();
  const suggestionsRef = useRef(null);
  const inputRef = useRef(null);

  // Load all drugs once on mount
  useEffect(() => {
    getAllDrugs().then(setAllDrugs).catch(() => setAllDrugs([]));
  }, []);

  // Helper to get drug data by name (synchronous, uses loaded cache)
  const getDrugByName = (name) => {
    if (!name || allDrugs.length === 0) return undefined;
    const lowerName = name.toLowerCase();
    return allDrugs.find(drug =>
      drug?.drug_metadata?.brand_name?.toLowerCase() === lowerName ||
      drug?.drug_metadata?.generic_name?.toLowerCase() === lowerName
    );
  };

  const popularMedicationsList = [
    { name: "Humira", category: "Arthritis Medication", icon: "💊" },
    { name: "Xarelto", category: "Blood Thinner", icon: "💊" },
    { name: "Januvia", category: "Diabetes Medication", icon: "💊" },
    { name: "Advair", category: "Asthma Medication", icon: "💊" }
  ];

  const popularMedications = useMemo(() => {
    return popularMedicationsList.map(med => {
      const drug = getDrugByName(med.name);
      let usPrice = 0;
      let globalPrice = 0;

      if (drug) {
        let firstDosage = null;
        for (const r of drug.regions || []) {
          for (const p of r.pharmacies || []) {
            if (p.dosages && p.dosages.length > 0) {
              firstDosage = p.dosages[0].dosage_strength;
              break;
            }
          }
          if (firstDosage) break;
        }

        const usPrices = [];
        const euPrices = [];
        const asiaPrices = [];

        for (const r of drug.regions || []) {
          const rName = r.region_name?.toLowerCase() || "";
          let targetArray = null;

          if (rName.includes("usa") || rName.includes("north america")) targetArray = usPrices;
          else if (rName.includes("europe")) targetArray = euPrices;
          else targetArray = asiaPrices;

          for (const p of r.pharmacies || []) {
            for (const dos of p.dosages || []) {
              const price = dos.price_usd ?? dos.price_with_coupon_usd ?? dos.price_cash_usd;
              if (dos.dosage_strength === firstDosage && price !== undefined) {
                targetArray.push(parseFloat(price));
              }
            }
          }
        }

        if (usPrices.length > 0) {
          usPrice = usPrices.reduce((a, b) => a + b, 0) / usPrices.length;
        }

        const euAvg = euPrices.length > 0 ? euPrices.reduce((a, b) => a + b, 0) / euPrices.length : 0;
        const asiaAvg = asiaPrices.length > 0 ? asiaPrices.reduce((a, b) => a + b, 0) / asiaPrices.length : 0;

        const validGlobal = [euAvg, asiaAvg].filter(p => p > 0);
        if (validGlobal.length > 0) {
          globalPrice = Math.min(...validGlobal);
        }
      }

      return {
        ...med,
        usPrice: usPrice > 0
          ? `$${usPrice.toLocaleString("en-US", { maximumFractionDigits: 0 })} US`
          : "N/A",
        globalPrice: globalPrice > 0
          ? `$${globalPrice.toLocaleString("en-US", { maximumFractionDigits: 0 })} Global`
          : "N/A"
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDrugs]);

  const [showPopular, setShowPopular] = useState(false);

  useEffect(() => {
    if (searchQuery.length >= 3) {
      const lowerQuery = searchQuery.toLowerCase();
      // Filter drugs by brand_name or generic_name from loaded data
      const filteredDrugs = allDrugs.filter(drug =>
        drug?.drug_metadata?.brand_name?.toLowerCase()?.includes(lowerQuery) ||
        drug?.drug_metadata?.generic_name?.toLowerCase()?.includes(lowerQuery)
      );
      const filteredSuggestions = filteredDrugs
        .map(drug => drug?.drug_metadata?.brand_name)
        .filter(Boolean);
      setSuggestions(filteredSuggestions);
      setShowSuggestions(true);
      setShowPopular(false);
      setSearchError("");
      setActiveSuggestionIndex(-1);
    } else if (searchQuery.length === 0 && showPopular) {
      setSuggestions([]);
      setShowSuggestions(false);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [searchQuery, showPopular, allDrugs]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
        setShowPopular(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSearch = (query = searchQuery) => {
    if (query.trim().length < 3) {
      setSearchError("Please enter at least 3 characters.");
      setShowSuggestions(false);
      setShowPopular(false);
      return;
    }

    if (query.trim()) {
      navigate(`/price-compare?medicine=${encodeURIComponent(query)}`);
      setShowSuggestions(false);
      setShowPopular(false);
      setSearchError(""); // 🧹 Clear error on success
    }
  };

  const handleOpenRequestModal = () => {
    setRequestModalOpen(true);
    setRequestStatus({ loading: false, success: false, error: "" });
  };

  const handleCloseRequestModal = () => {
    setRequestModalOpen(false);
  };

  const handleSubmitRequest = async () => {
    if (!searchQuery.trim()) return;
    setRequestStatus({ loading: true, success: false, error: "" });
    try {
      const response = await fetch("http://localhost:5001/api/drug-requests", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          drug_name: searchQuery,
          email: requestEmail
        })
      });
      const data = await response.json();
      if (data.success) {
        setRequestStatus({ loading: false, success: true, error: "" });
        setTimeout(() => {
          handleCloseRequestModal();
          setShowSuggestions(false);
          setSearchQuery("");
        }, 2000);
      } else {
        setRequestStatus({ loading: false, success: false, error: data.error || "Failed to submit request" });
      }
    } catch (err) {
      setRequestStatus({ loading: false, success: false, error: "Network error. Please try again later." });
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion);
    setShowSuggestions(false);
    setShowPopular(false);
    handleSearch(suggestion);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions && !showPopular) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (showSuggestions) {
          setActiveSuggestionIndex(prev =>
            prev < suggestions.length - 1 ? prev + 1 : prev
          );
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (showSuggestions) {
          setActiveSuggestionIndex(prev => (prev > 0 ? prev - 1 : -1));
        }
        break;
      case "Enter":
        e.preventDefault();
        if (activeSuggestionIndex >= 0 && showSuggestions) {
          handleSuggestionClick(suggestions[activeSuggestionIndex]);
        } else {
          handleSearch();
        }
        break;
      case "Escape":
        setShowSuggestions(false);
        setShowPopular(false);
        setActiveSuggestionIndex(-1);
        break;
      default:
        break;
    }
  };

  const location = useLocation();
  const isActive = (path) => (location.pathname === path ? "active" : "");

  return (
    <div className="hero_section_main" style={{ background: "#E0FAF8" }}>
      <Container>
        <div className="hero_content" style={{ position: "relative", zIndex: 100 }}>
          <Typography variant="h4" className="heading_two" gutterBottom>
            Exposing Prescription Drug Price Disparities <br />
            <Typography variant="span" className="green_text">
              Why do Americans pay more<br />
              for prescription drugs?
            </Typography>
          </Typography>

          <div className="form_search" style={{ position: "relative", zIndex: 100 }}>
            <TextField
              ref={inputRef}
              variant="outlined"
              placeholder="Enter a Medicine Name"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setSearchError(""); // 🧹 Clear error on change
                if (e.target.value.length === 0) {
                  setShowPopular(true);
                }
              }}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                if (searchQuery.length === 0) {
                  setShowPopular(true);
                } else if (searchQuery.length >= 3) {
                  setShowSuggestions(true);
                }
              }}
              sx={{ marginRight: "1rem", flexGrow: 1 }}
              autoComplete="off"
              error={!!searchError}
              helperText={searchError}
            />

            {/* Popular Medications Dropdown */}
            {showPopular && searchQuery.length === 0 && (
              <div
                ref={suggestionsRef}
                style={{
                  width: "100%",
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  backgroundColor: "white",
                  borderRadius: "12px",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                  zIndex: 9999,
                  overflow: "hidden",
                  marginTop: "8px",
                  border: "1px solid #eee"
                }}
              >
                <div style={{
                  padding: "16px",
                  textAlign: "center",
                  borderBottom: "1px solid #f0f0f0",
                  fontWeight: "700",
                  fontSize: "18px",
                  color: "#1a202c"
                }}>
                  Popular Medications
                </div>
                <div style={{ maxHeight: "155px", overflowY: "auto" }}>
                  {popularMedications.map((med, index) => (
                    <div
                      key={index}
                      onClick={() => handleSuggestionClick(med.name)}
                      style={{
                        padding: "16px 24px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        transition: "background 0.2s",
                        borderBottom: index < popularMedications.length - 1 ? "1px solid #f7fafc" : "none"
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f7fafc"}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "white"}
                    >
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <div style={{
                          width: "40px",
                          height: "40px",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "20px",
                          marginRight: "16px"
                        }}>
                          {med.icon}
                        </div>
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <span style={{ fontWeight: "600", fontSize: "16px", color: "#2d3748" }}>{med.name}</span>
                          <span style={{ fontSize: "14px", color: "#718096" }}>{med.category}</span>
                        </div>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                        <span style={{ fontWeight: "700", fontSize: "16px", color: "#BD2727" }}>{med.usPrice}</span>
                        <span style={{ fontWeight: "700", fontSize: "16px", color: "#2771D9" }}>{med.globalPrice}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Autocomplete Suggestions */}
            {showSuggestions && (
              <div
                ref={suggestionsRef}
                style={{
                  width: "100%",
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  backgroundColor: "white",
                  border: "1px solid #ccc",
                  borderTop: "none",
                  borderRadius: "0 0 4px 4px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  zIndex: 1000,
                  maxHeight: "300px",
                  overflowY: "auto",
                  marginTop: "8px"
                }}
              >
                {suggestions.length > 0 ? (
                  <div style={{
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    borderRadius: "12px",
                    padding: "8px",
                    boxShadow: "0 8px 32px rgba(102, 126, 234, 0.3)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.2)"
                  }}>
                    {suggestions.map((suggestion, index) => (
                      <div
                        key={suggestion}
                        onClick={() => handleSuggestionClick(suggestion)}
                        style={{
                          margin: "4px 0",
                          padding: "16px 20px",
                          cursor: "pointer",
                          background: index === activeSuggestionIndex
                            ? "linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.85))"
                            : "linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.7))",
                          borderRadius: "8px",
                          display: "flex",
                          flexDirection: "column",
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          transform: index === activeSuggestionIndex ? "translateY(-2px) scale(1.02)" : "translateY(0) scale(1)",
                          boxShadow: index === activeSuggestionIndex
                            ? "0 8px 25px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(102, 126, 234, 0.4)"
                            : "0 2px 8px rgba(0, 0, 0, 0.1)",
                          backdropFilter: "blur(10px)",
                          border: index === activeSuggestionIndex
                            ? "2px solid rgba(102, 126, 234, 0.6)"
                            : "1px solid rgba(255, 255, 255, 0.3)",
                          position: "relative",
                          overflow: "hidden"
                        }}
                        onMouseEnter={() => setActiveSuggestionIndex(index)}
                        onMouseLeave={() => setActiveSuggestionIndex(-1)}
                      >
                        {/* Animated background gradient on hover */}
                        <div style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: "linear-gradient(45deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1))",
                          opacity: index === activeSuggestionIndex ? 1 : 0,
                          transition: "opacity 0.3s ease",
                          borderRadius: "6px"
                        }} />

                        {/* Medicine icon */}
                        <div style={{
                          position: "absolute",
                          right: "16px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          fontSize: "20px",
                          opacity: 0.6,
                          color: "#667eea"
                        }}>
                          💊
                        </div>

                        <div style={{ position: "relative", zIndex: 1, paddingRight: "40px" }}>
                          <div style={{
                            display: "flex",
                            alignItems: "center",
                            marginBottom: "6px"
                          }}>
                            <div style={{
                              width: "4px",
                              height: "16px",
                              background: "linear-gradient(135deg, #667eea, #764ba2)",
                              borderRadius: "2px",
                              marginRight: "12px",
                              opacity: index === activeSuggestionIndex ? 1 : 0.6,
                              transition: "opacity 0.3s ease"
                            }} />
                            <span style={{
                              fontWeight: "600",
                              fontSize: "15px",
                              color: "#2d3748",
                              letterSpacing: "0.025em"
                            }}>
                              {suggestion}
                            </span>
                          </div>

                          <span style={{
                            fontSize: "13px",
                            color: "#4a5568",
                            lineHeight: "1.4",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            marginLeft: "16px",
                            opacity: 0.8
                          }}>
                            {(() => {
                              const drug = getDrugByName(suggestion);
                              if (drug) {
                                return `${drug.drug_metadata.generic_name} - ${drug.drug_metadata.drug_class}`;
                              }
                              return "Medication information available";
                            })()}
                          </span>
                        </div>

                        {/* Shimmer effect */}
                        <div style={{
                          position: "absolute",
                          top: 0,
                          left: "-100%",
                          width: "100%",
                          height: "100%",
                          background: "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)",
                          animation: index === activeSuggestionIndex ? "shimmer 2s infinite" : "none"
                        }} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{
                    padding: "24px",
                    textAlign: "center",
                    background: "linear-gradient(135deg, #f7fafc, #edf2f7)",
                    borderRadius: "12px",
                    border: "2px dashed #cbd5e0",
                    color: "#718096"
                  }}>
                    <div style={{ fontSize: "32px", marginBottom: "8px", opacity: 0.6 }}>🔍</div>
                    <div style={{ fontSize: "14px", fontWeight: "500" }}>No medicines found</div>
                    <div style={{ fontSize: "12px", marginTop: "4px", opacity: 0.7, marginBottom: "16px" }}>
                      Try searching for a different medication name
                    </div>
                    <Button variant="outlined" color="primary" onClick={handleOpenRequestModal} size="small" sx={{ borderRadius: "20px" }}>
                      Request Drug
                    </Button>
                  </div>
                )}

                {/* Add this CSS for the shimmer animation */}
                <style jsx>{`
  @keyframes shimmer {
    0% {
      left: -100%;
    }
    100% {
      left: 100%;
    }
  }
`}</style>
              </div>
            )}

            <Button variant="contained" color="primary" onClick={() => handleSearch()}>
              Compare Prices
            </Button>
          </div>

          <ButtonLink
            url="/medicines"
            title="Browse Popular Medications"
          />
        </div>
      </Container>

      {/* Drug Request Modal */}
      <Dialog open={requestModalOpen} onClose={handleCloseRequestModal} maxWidth="sm" fullWidth>
        <DialogTitle>Request Medication</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Can't find <strong>{searchQuery}</strong>? Let us know and we'll work on adding it.
            You can optionally provide your email to be notified when it's available.
          </DialogContentText>
          <TextField
            margin="dense"
            label="Medication Name"
            type="text"
            fullWidth
            variant="outlined"
            value={searchQuery}
            disabled
            sx={{ mt: 3 }}
          />
          <TextField
            margin="dense"
            label="Email Address (Optional)"
            type="email"
            fullWidth
            variant="outlined"
            value={requestEmail}
            onChange={(e) => setRequestEmail(e.target.value)}
            sx={{ mt: 2 }}
          />
          {requestStatus.error && (
            <Typography color="error" variant="body2" sx={{ mt: 2 }}>
              {requestStatus.error}
            </Typography>
          )}
          {requestStatus.success && (
            <Typography color="success.main" variant="body2" sx={{ mt: 2 }}>
              Request submitted successfully!
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseRequestModal} disabled={requestStatus.loading}>Cancel</Button>
          <Button onClick={handleSubmitRequest} variant="contained" disabled={requestStatus.loading || requestStatus.success}>
            {requestStatus.loading ? "Submitting..." : "Submit Request"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
